import {useCallback, useEffect, useState} from 'react'
import {BASE_URL} from '../../../api'
import config from '../../config'
import uploadFileSecurely from '../../helper/secureUpload'
import {aiDraft, aiError} from '../_shared/aiDraft'
const {axios} = config

// Roadmap #14 (2026-09-24): location-targeted push notifications. Pick
// towns (each user's own chosen town) and audience, write the message in
// English and Spanish, send now or schedule. Everyone also gets it in
// their in-app notifications.
const STATUS = {
  scheduled: ['Scheduled', 'info'],
  sending: ['Sending…', 'warning'],
  sent: ['Sent', 'success'],
  canceled: ['Canceled', 'secondary'],
  failed: ['Failed', 'danger'],
}
const townLabel = (t) => String(t).replace(/_/g, ' ')
const fmt = (d) => (d ? new Date(d).toLocaleString() : '—')
// Date -> value for a datetime-local input (this computer's time zone).
const toLocalInput = (d) => {
  const x = new Date(d)
  const p = (n) => String(n).padStart(2, '0')
  return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}T${p(x.getHours())}:${p(x.getMinutes())}`
}
// Can this message still be edited? Scheduled, and (local-time sends) no
// time zone has gone out yet.
const canEdit = (c) => c.status === 'scheduled' && c.scheduledAt && !(c.slots || []).some((s) => s.status !== 'pending')

const empty = {title: '', body: '', titleEs: '', bodyEs: '', imageUrl: ''}
const noLink = {linkType: '', linkId: '', linkLabel: ''}

// Optional: tapping the notification opens a practitioner/business or a class.
function LinkPicker({link, setLink}) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  useEffect(() => {
    if (!link.linkType || q.trim().length < 2) return setResults([])
    const t = setTimeout(async () => {
      try {
        const url = link.linkType === 'class' ? `${BASE_URL}/admin/push/link-classes` : `${BASE_URL}/admin/text/search`
        const {data} = await axios.get(url, {params: {q}})
        setResults(data.data || [])
      } catch (e) {
        setResults([])
      }
    }, 300)
    return () => clearTimeout(t)
  }, [q, link.linkType])
  const label = (r) =>
    link.linkType === 'class'
      ? `${r.name} · ${new Date(r.startTime).toLocaleString([], {weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit'})}${r.host ? ` · ${r.host}` : ''}`
      : `${r.name}${r.town ? ` · ${r.town}` : ''}`
  return (
    <div>
      <div className='d-flex align-items-center gap-6 mb-2'>
        {[['', 'Just open the app'], ['practitioner', 'A practitioner / business'], ['class', 'A class or event']].map(([v, l]) => (
          <label key={v} className='form-check form-check-custom form-check-solid'>
            <input
              className='form-check-input'
              type='radio'
              checked={link.linkType === v}
              onChange={() => {
                setLink({...noLink, linkType: v})
                setQ('')
              }}
            />
            <span className='form-check-label'>{l}</span>
          </label>
        ))}
      </div>
      {link.linkType && link.linkId && (
        <div className='d-flex align-items-center gap-3'>
          <span className='badge badge-light-primary fs-7 py-3 px-4'>Opens: {link.linkLabel}</span>
          <button type='button' className='btn btn-sm btn-light' onClick={() => setLink({...noLink, linkType: link.linkType})}>
            Change
          </button>
        </div>
      )}
      {link.linkType && !link.linkId && (
        <div className='position-relative' style={{maxWidth: 520}}>
          <input
            className='form-control form-control-solid'
            placeholder={link.linkType === 'class' ? 'Search class name or host…' : 'Search name or business…'}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {results.length > 0 && (
            <div className='border rounded bg-body shadow-sm position-absolute w-100 mt-1' style={{zIndex: 10, maxHeight: 300, overflowY: 'auto'}}>
              {results.map((r) => (
                <div
                  key={r._id}
                  className='px-4 py-3 border-bottom cursor-pointer bg-hover-light'
                  onClick={() => {
                    setLink({linkType: link.linkType, linkId: r._id, linkLabel: label(r)})
                    setResults([])
                    setQ('')
                  }}>
                  {label(r)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className='text-muted fs-7 mt-2'>When someone taps the notification, the app opens straight to this page.</div>
    </div>
  )
}

// Shrink big photos before upload (push images should stay well under 1 MB).
const shrinkImage = (file, maxW = 1200) =>
  new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(1, maxW / img.width)
      const c = document.createElement('canvas')
      c.width = Math.round(img.width * scale)
      c.height = Math.round(img.height * scale)
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height)
      c.toBlob(
        (b) => resolve(b ? new File([b], file.name.replace(/\.\w+$/, '') + '.jpg', {type: 'image/jpeg'}) : file),
        'image/jpeg',
        0.82
      )
    }
    img.onerror = () => resolve(file)
    img.src = URL.createObjectURL(file)
  })

const PushNotifications = () => {
  const [towns, setTowns] = useState([])
  const [selTowns, setSelTowns] = useState([]) // empty = all towns
  const [audience, setAudience] = useState(['clients'])
  const [msg, setMsg] = useState(empty)
  const [link, setLink] = useState(noLink)
  // AI writing helper (roadmap #17.4)
  const [idea, setIdea] = useState('')
  const [drafting, setDrafting] = useState(false)
  const [options, setOptions] = useState([])
  const [when, setWhen] = useState('now')
  const [scheduledAt, setScheduledAt] = useState('')
  const [localDate, setLocalDate] = useState('')
  const [localTime, setLocalTime] = useState('08:00')
  const [counts, setCounts] = useState(null)
  const [history, setHistory] = useState([])
  const [busy, setBusy] = useState(false)
  const [translating, setTranslating] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [editing, setEditing] = useState(null) // the scheduled message being edited

  const loadHistory = useCallback(async () => {
    try {
      const {data} = await axios.get(`${BASE_URL}/admin/push/campaigns`)
      setHistory(data.data || [])
    } catch (e) {
      setError('Could not load past messages.')
    }
  }, [])

  useEffect(() => {
    axios
      .get(`${BASE_URL}/admin/push/towns`)
      .then(({data}) => setTowns(data.data || []))
      .catch(() => setError('Could not load towns.'))
    loadHistory()
  }, [loadHistory])

  // Refresh history while something is sending.
  useEffect(() => {
    if (!history.some((h) => h.status === 'sending')) return
    const t = setInterval(loadHistory, 5000)
    return () => clearInterval(t)
  }, [history, loadHistory])

  useEffect(() => {
    const t = setTimeout(() => {
      axios
        .get(`${BASE_URL}/admin/push/audience`, {
          params: {towns: selTowns.join(','), audience: audience.join(',')},
        })
        .then(({data}) => setCounts(data.data))
        .catch(() => setCounts(null))
    }, 200)
    return () => clearTimeout(t)
  }, [selTowns, audience])

  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v])

  const translate = async () => {
    if (!msg.title && !msg.body) return
    setTranslating(true)
    try {
      const {data} = await axios.post(`${BASE_URL}/admin/push/translate`, {title: msg.title, body: msg.body})
      setMsg((m) => ({...m, titleEs: data.data.titleEs, bodyEs: data.data.bodyEs}))
    } catch (e) {
      setError('Could not translate - please write the Spanish yourself.')
    } finally {
      setTranslating(false)
    }
  }

  const onPickImage = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) return setError('Please choose an image file.')
    setUploading(true)
    setError('')
    try {
      const small = await shrinkImage(file)
      const {fileUrl} = await uploadFileSecurely(small)
      setMsg((m) => ({...m, imageUrl: fileUrl}))
    } catch (err) {
      setError('Could not upload the image.')
    } finally {
      setUploading(false)
    }
  }

  const sendTest = async () => {
    setError('')
    setNotice('')
    if (!msg.title.trim() || !msg.body.trim()) return setError('Add an English title and message first.')
    if (!testEmail.trim()) return setError('Enter the email of the app account to test with.')
    setTesting(true)
    try {
      const {data} = await axios.post(`${BASE_URL}/admin/push/test`, {...msg, ...link, email: testEmail.trim()})
      setNotice(`${data?.message || 'Test sent.'} (Only ${testEmail.trim()} gets it.)`)
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not send the test.')
    } finally {
      setTesting(false)
    }
  }

  const send = async () => {
    setError('')
    setNotice('')
    if (!msg.title.trim() || !msg.body.trim()) return setError('Add an English title and message.')
    if (!audience.length) return setError('Choose clients and/or practitioners.')
    if (when === 'later' && !scheduledAt) return setError('Pick a date and time.')
    if (when === 'local' && (!localDate || !localTime)) return setError('Pick a date and a local time.')
    const where = selTowns.length ? selTowns.map(townLabel).join(', ') : 'ALL towns'
    const whenText =
      when === 'later'
        ? `on ${new Date(scheduledAt).toLocaleString()}`
        : when === 'local'
        ? `on ${localDate} at ${localTime} in each person's local time`
        : 'right now'
    const question = editing
      ? `Save changes? "${msg.title}" will go to ${counts?.total ?? '?'} people in ${where} ${whenText}.`
      : `Send "${msg.title}" to ${counts?.total ?? '?'} people in ${where} ${whenText}?`
    if (!window.confirm(question)) return
    setBusy(true)
    try {
      const url = editing ? `${BASE_URL}/admin/push/campaigns/${editing._id}` : `${BASE_URL}/admin/push/campaigns`
      await axios[editing ? 'put' : 'post'](url, {
        ...msg,
        ...link,
        towns: selTowns,
        audience,
        scheduledAt: when === 'later' ? new Date(scheduledAt).toISOString() : null,
        ...(when === 'local' ? {localDate, localTime} : {}),
      })
      setNotice(
        editing && when !== 'now'
          ? 'Changes saved.'
          : when === 'later'
          ? 'Scheduled.'
          : when === 'local'
          ? `Scheduled for ${localTime} local time in each town.`
          : 'Sending now - it can take a minute for a big town.'
      )
      resetForm()
      await loadHistory()
    } catch (e) {
      setError(e?.response?.data?.message || (editing ? 'Could not save the changes.' : 'Could not send.'))
      if (editing) loadHistory()
    } finally {
      setBusy(false)
    }
  }

  const resetForm = () => {
    setMsg(empty)
    setLink(noLink)
    setWhen('now')
    setScheduledAt('')
    setEditing(null)
  }

  const startEdit = (c) => {
    setError('')
    setNotice('')
    setOptions([])
    setEditing(c)
    setMsg({title: c.title || '', body: c.body || '', titleEs: c.titleEs || '', bodyEs: c.bodyEs || '', imageUrl: c.imageUrl || ''})
    setLink({linkType: c.linkType || '', linkId: c.linkId || '', linkLabel: c.linkLabel || ''})
    setSelTowns(c.towns || [])
    setAudience(c.audience?.length ? c.audience : ['clients'])
    if (c.localTime) {
      setWhen('local')
      setLocalDate(c.localDate || '')
      setLocalTime(c.localTime)
    } else {
      setWhen('later')
      setScheduledAt(toLocalInput(c.scheduledAt))
    }
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  const cancel = async (c) => {
    if (!window.confirm(`Cancel "${c.title}"?`)) return
    try {
      await axios.put(`${BASE_URL}/admin/push/campaigns/${c._id}/cancel`)
      await loadHistory()
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not cancel.')
    }
  }

  return (
    <>
      <div className='card mb-5 mb-xl-10'>
        <div className='card-header border-0 pt-6'>
          <div className='card-title m-0'>
            <h3 className='fw-bolder m-0'>Push Notifications</h3>
          </div>
        </div>
        <div className='card-body border-top p-9'>
          <p className='text-muted'>
            Send a push notification to people in specific towns (the town each person chose in the
            app). It also appears in their in-app notifications. People get it in their app language;
            if we don't know it yet, they get both.
          </p>
          {editing && (
            <div className='alert alert-warning d-flex justify-content-between align-items-center py-3'>
              <span>
                Editing the scheduled message <b>"{editing.title}"</b>. Save to update it, or stop editing to leave it as it was.
              </span>
              <button type='button' className='btn btn-sm btn-light' onClick={resetForm}>
                Stop editing
              </button>
            </div>
          )}
          {error && <div className='alert alert-danger py-3'>{error}</div>}
          {notice && <div className='alert alert-success py-3'>{notice}</div>}

          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label fw-bold'>Towns</label>
            <div className='col-lg-10'>
              <div className='d-flex flex-wrap gap-2'>
                <button
                  type='button'
                  className={`btn btn-sm ${selTowns.length === 0 ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => setSelTowns([])}
                >
                  All towns
                </button>
                {towns.map((t) => (
                  <button
                    key={t.value}
                    type='button'
                    className={`btn btn-sm ${selTowns.includes(t.value) ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => toggle(selTowns, setSelTowns, t.value)}
                  >
                    {t.label} <span className='opacity-75'>({t.users})</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label fw-bold'>Send to</label>
            <div className='col-lg-10 d-flex align-items-center gap-6'>
              {[
                ['clients', 'Clients'],
                ['practitioners', 'Practitioners & businesses'],
              ].map(([v, l]) => (
                <label key={v} className='form-check form-check-custom form-check-solid'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={audience.includes(v)}
                    onChange={() => toggle(audience, setAudience, v)}
                  />
                  <span className='form-check-label'>{l}</span>
                </label>
              ))}
              {counts && (
                <span className='badge badge-light-primary fs-7'>
                  {counts.total} people · {counts.withPush} with push turned on
                </span>
              )}
            </div>
          </div>

          <div className='rounded p-5 mb-6' style={{background: '#F8F4EF'}}>
            <div className='fw-bold mb-2'>✨ Write it for me</div>
            <div className='d-flex gap-2'>
              <input
                className='form-control form-control-solid'
                placeholder='Your idea, e.g. remind Sayulita people there are sound baths this weekend'
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
              />
              <button
                type='button'
                className='btn btn-light-primary text-nowrap'
                disabled={drafting || idea.trim().length < 3}
                onClick={async () => {
                  setDrafting(true)
                  setError('')
                  try {
                    const d = await aiDraft('push', idea)
                    setOptions(Array.isArray(d?.options) ? d.options : [])
                  } catch (e) {
                    setError(aiError(e))
                  } finally {
                    setDrafting(false)
                  }
                }}
              >
                {drafting ? 'Writing…' : 'Write'}
              </button>
            </div>
            {options.length > 0 && (
              <div className='row g-3 mt-1'>
                {options.map((o, i) => (
                  <div className='col-lg-4' key={i}>
                    <div className='border rounded p-3 h-100 bg-white d-flex flex-column'>
                      <div className='fw-bold'>{o.title}</div>
                      <div className='fs-7 mb-2'>{o.body}</div>
                      <div className='fw-bold text-muted fs-7'>{o.titleEs}</div>
                      <div className='fs-7 text-muted mb-3'>{o.bodyEs}</div>
                      <button
                        type='button'
                        className='btn btn-sm btn-primary mt-auto'
                        onClick={() => {
                          setMsg((m) => ({...m, title: o.title || '', body: o.body || '', titleEs: o.titleEs || '', bodyEs: o.bodyEs || ''}))
                          setOptions([])
                        }}
                      >
                        Use this
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className='text-muted fs-8 mt-2'>Drafts only - review and edit below before sending.</div>
          </div>

          <div className='row mb-6'>
            <div className='col-lg-6'>
              <div className='fw-bold mb-2'>English</div>
              <input
                className='form-control form-control-solid mb-3'
                placeholder='Title (e.g. New in Tulum this week)'
                maxLength={80}
                value={msg.title}
                onChange={(e) => setMsg({...msg, title: e.target.value})}
              />
              <textarea
                className='form-control form-control-solid'
                rows={4}
                maxLength={400}
                placeholder='Message'
                value={msg.body}
                onChange={(e) => setMsg({...msg, body: e.target.value})}
              />
            </div>
            <div className='col-lg-6'>
              <div className='d-flex justify-content-between mb-2'>
                <span className='fw-bold'>Español</span>
                <button type='button' className='btn btn-sm btn-light' disabled={translating} onClick={translate}>
                  {translating ? 'Translating…' : 'Translate from English'}
                </button>
              </div>
              <input
                className='form-control form-control-solid mb-3'
                placeholder='Título'
                maxLength={80}
                value={msg.titleEs}
                onChange={(e) => setMsg({...msg, titleEs: e.target.value})}
              />
              <textarea
                className='form-control form-control-solid'
                rows={4}
                maxLength={400}
                placeholder='Mensaje'
                value={msg.bodyEs}
                onChange={(e) => setMsg({...msg, bodyEs: e.target.value})}
              />
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label fw-bold'>Image (optional)</label>
            <div className='col-lg-10'>
              <div className='d-flex align-items-center gap-3'>
                <label className='btn btn-sm btn-light mb-0'>
                  {uploading ? 'Uploading…' : 'Upload image'}
                  <input type='file' accept='image/*' hidden disabled={uploading} onChange={onPickImage} />
                </label>
                <input
                  className='form-control form-control-solid'
                  placeholder='…or paste an image link (https://)'
                  value={msg.imageUrl}
                  onChange={(e) => setMsg({...msg, imageUrl: e.target.value.trim()})}
                />
                {msg.imageUrl && (
                  <button type='button' className='btn btn-sm btn-light-danger' onClick={() => setMsg({...msg, imageUrl: ''})}>
                    Remove
                  </button>
                )}
              </div>
              {msg.imageUrl && (
                <img src={msg.imageUrl} alt='' className='mt-3 rounded' style={{maxWidth: 320, maxHeight: 180, objectFit: 'cover'}} />
              )}
              <div className='text-muted fs-7 mt-2'>
                Shows as a picture in the notification (wide images look best, e.g. 1200×600). iPhones need app 2.39 or newer to show it; older iPhones just show the text.
              </div>
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label fw-bold'>Opens (optional)</label>
            <div className='col-lg-10'>
              <LinkPicker link={link} setLink={setLink} />
            </div>
          </div>

          <div className='row mb-6'>
            <label className='col-lg-2 col-form-label fw-bold'>When</label>
            <div className='col-lg-10 d-flex align-items-center gap-6'>
              <label className='form-check form-check-custom form-check-solid'>
                <input className='form-check-input' type='radio' checked={when === 'now'} onChange={() => setWhen('now')} />
                <span className='form-check-label'>Send now</span>
              </label>
              <label className='form-check form-check-custom form-check-solid'>
                <input className='form-check-input' type='radio' checked={when === 'later'} onChange={() => setWhen('later')} />
                <span className='form-check-label'>Schedule</span>
              </label>
              {when === 'later' && (
                <input
                  type='datetime-local'
                  className='form-control form-control-solid w-250px'
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                />
              )}
              {when === 'later' && <span className='text-muted fs-7'>Your computer's time zone</span>}
              <label className='form-check form-check-custom form-check-solid'>
                <input className='form-check-input' type='radio' checked={when === 'local'} onChange={() => setWhen('local')} />
                <span className='form-check-label'>Schedule at local time</span>
              </label>
              {when === 'local' && (
                <>
                  <input type='date' className='form-control form-control-solid w-175px' value={localDate} onChange={(e) => setLocalDate(e.target.value)} />
                  <input type='time' className='form-control form-control-solid w-125px' value={localTime} onChange={(e) => setLocalTime(e.target.value)} />
                </>
              )}
            </div>
            {when === 'local' && (
              <div className='col-lg-10 offset-lg-2 text-muted fs-7 mt-2'>
                Everyone gets it at this time where they are right now - travellers too (by their phone's time zone, app 2.42+; otherwise their town's). It goes out zone by zone as each one reaches that time. If the time has already passed somewhere, it goes out there straight away.
              </div>
            )}
          </div>

          <div className='d-flex justify-content-end align-items-center gap-3'>
            <input
              className='form-control form-control-solid w-250px'
              placeholder='Your app account email'
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
            <button className='btn btn-light' disabled={testing} onClick={sendTest}>
              {testing ? 'Sending test…' : 'Send test to me'}
            </button>
            <button className='btn btn-primary' disabled={busy} onClick={send}>
              {busy ? (editing ? 'Saving…' : 'Sending…') : when === 'now' ? 'Send now' : editing ? 'Save changes' : 'Schedule'}
            </button>
          </div>
        </div>
      </div>

      <div className='card mb-5 mb-xl-10'>
        <div className='card-header border-0 pt-6'>
          <div className='card-title m-0'>
            <h3 className='fw-bolder m-0'>Sent & scheduled</h3>
          </div>
        </div>
        <div className='card-body border-top p-9'>
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-4'>
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase'>
                  <th>Message</th>
                  <th>Towns</th>
                  <th>Audience</th>
                  <th>When</th>
                  <th>Reached</th>
                  <th>Status</th>
                  <th className='text-end'></th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr>
                    <td colSpan={7} className='text-muted'>Nothing sent yet.</td>
                  </tr>
                )}
                {history.map((c) => {
                  const [label, color] = STATUS[c.status] || [c.status, 'secondary']
                  return (
                    <tr key={c._id}>
                      <td>
                        <div className='d-flex gap-3'>
                          {c.imageUrl && <img src={c.imageUrl} alt='' className='rounded' style={{width: 48, height: 48, objectFit: 'cover'}} />}
                          <div>
                            <div className='fw-bold'>{c.title}</div>
                            <div className='text-muted fs-7'>{c.body}</div>
                            {c.linkLabel && <div className='text-primary fs-7'>Opens: {c.linkLabel}</div>}
                          </div>
                        </div>
                      </td>
                      <td>{c.towns?.length ? c.towns.map(townLabel).join(', ') : 'All towns'}</td>
                      <td>{(c.audience || []).map((a) => (a === 'clients' ? 'Clients' : 'Practitioners')).join(' + ')}</td>
                      <td>
                        {c.localTime ? (
                          <>
                            <div>
                              {c.localDate} · {c.localTime} local
                            </div>
                            <div className='text-muted fs-7'>
                              {(c.slots || []).filter((x) => x.status === 'sent').length}/{(c.slots || []).length} time zones sent
                            </div>
                          </>
                        ) : (
                          fmt(c.sentAt || c.scheduledAt || c.createdAt)
                        )}
                        {c.editedAt && <div className='text-muted fs-8'>Edited {fmt(c.editedAt)}</div>}
                      </td>
                      <td>
                        {c.status === 'sent' ? (
                          <>
                            <div>{c.recipientCount} in-app</div>
                            {c.pushDelivered || c.pushFailed ? (
                              <>
                                <div className='text-success fs-7'>{c.pushDelivered} phones delivered</div>
                                {c.pushFailed > 0 && (
                                  <div className='text-danger fs-7' title={JSON.stringify(c.pushErrors || {})}>
                                    {c.pushFailed} failed ({Object.entries(c.pushErrors || {}).map(([k, v]) => `${k}: ${v}`).join(', ')})
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className='text-muted fs-7'>{c.pushCount} push attempted</div>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <span className={`badge badge-light-${color}`}>{label}</span>
                        {c.error && <div className='text-danger fs-7'>{c.error}</div>}
                      </td>
                      <td className='text-end'>
                        <div className='d-flex justify-content-end gap-2'>
                          {canEdit(c) && (
                            <button className='btn btn-sm btn-light-primary' disabled={editing?._id === c._id} onClick={() => startEdit(c)}>
                              {editing?._id === c._id ? 'Editing…' : 'Edit'}
                            </button>
                          )}
                          {c.status === 'scheduled' && c.scheduledAt && (
                            <button className='btn btn-sm btn-light-danger' onClick={() => cancel(c)}>
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}

export default PushNotifications
